import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getAiQueries, type AiQueryLogRow } from "../lib/api.ts";

const STATUS_COLORS: Record<string, string> = {
  success:     "badge-accent",
  unsupported: "badge-warn",
  timeout:     "badge-warn",
  unavailable: "badge-warn",
  error:       "badge-error",
};

const PAGE_SIZE = 50;

function fmt(ms: number | null): string {
  if (ms == null) return "-";
  return ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${ms}ms`;
}
function fmtTokens(n: number | null): string {
  if (n == null) return "-";
  return n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n);
}
function fmtDate(s: string): string {
  try { return new Date(s).toLocaleString(); } catch { return s; }
}
function truncate(s: string | null, len = 80): string {
  if (!s) return "-";
  return s.length > len ? s.slice(0, len) + "…" : s;
}

interface ToolCallStep { tool: string; params: Record<string, unknown> }

function parseSequence(json: string | null): ToolCallStep[] {
  if (!json) return [];
  try { return JSON.parse(json) as ToolCallStep[]; } catch { return []; }
}

function RowDetail({ row }: { row: AiQueryLogRow }) {
  const steps = parseSequence(row.tool_call_sequence);
  return (
    <div style={{ padding: "12px 16px", background: "var(--surface-2)", borderTop: "1px solid var(--border)" }}>
      {/* Query */}
      <div style={{ marginBottom: "10px" }}>
        <div className="label" style={{ marginBottom: "4px" }}>Query</div>
        <div style={{ fontSize: "13px", color: "var(--text)", lineHeight: 1.5, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
          {row.query}
        </div>
      </div>

      {/* Answer */}
      {row.answer && (
        <div style={{ marginBottom: "10px" }}>
          <div className="label" style={{ marginBottom: "4px" }}>Answer</div>
          <div style={{ fontSize: "12.5px", color: "var(--text-muted)", lineHeight: 1.6, whiteSpace: "pre-wrap", wordBreak: "break-word", maxHeight: "160px", overflowY: "auto" }}>
            {row.answer}
          </div>
        </div>
      )}

      {/* Tool call sequence */}
      {steps.length > 0 && (
        <div style={{ marginBottom: "10px" }}>
          <div className="label" style={{ marginBottom: "6px" }}>
            Tool call sequence ({steps.length} call{steps.length !== 1 ? "s" : ""})
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            {steps.map((step, i) => (
              <div key={i} style={{ display: "flex", gap: "8px", alignItems: "flex-start", fontSize: "12px" }}>
                <span style={{ color: "var(--text-subtle)", width: "18px", flexShrink: 0, paddingTop: "1px" }}>{i + 1}.</span>
                <code style={{ color: "var(--accent-text)", background: "var(--surface-raised)", padding: "1px 6px", borderRadius: "4px", flexShrink: 0, fontSize: "11.5px" }}>
                  {step.tool}
                </code>
                <span style={{ color: "var(--text-subtle)", fontFamily: "var(--font-mono)", fontSize: "11px", wordBreak: "break-all", lineHeight: 1.6 }}>
                  {JSON.stringify(step.params)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Metrics row */}
      <div style={{ display: "flex", gap: "20px", flexWrap: "wrap", fontSize: "12px", color: "var(--text-subtle)", marginTop: "8px", paddingTop: "8px", borderTop: "1px solid var(--border)" }}>
        {row.model && <span><strong>Model:</strong> {row.model}</span>}
        {row.provider && <span><strong>Provider:</strong> {row.provider}</span>}
        {row.input_tokens != null && <span><strong>In:</strong> {fmtTokens(row.input_tokens)} tok</span>}
        {row.output_tokens != null && <span><strong>Out:</strong> {fmtTokens(row.output_tokens)} tok</span>}
        {row.total_tokens != null && <span><strong>Total:</strong> {fmtTokens(row.total_tokens)} tok{row.estimated_tokens ? " (est)" : ""}</span>}
        {row.execution_ms != null && <span><strong>Duration:</strong> {fmt(row.execution_ms)}</span>}
        {row.history_allowed != null && <span><strong>History:</strong> {row.history_allowed ? "allowed" : "gated"}</span>}
        {row.byok != null && <span><strong>BYOK:</strong> {row.byok ? "yes" : "no"}</span>}
        {row.failure_reason && <span><strong>Reason:</strong> {row.failure_reason}</span>}
      </div>
    </div>
  );
}

export function AiQueriesPage() {
  const [items, setItems]       = useState<AiQueryLogRow[]>([]);
  const [total, setTotal]       = useState(0);
  const [offset, setOffset]     = useState(0);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState<string | null>(null);
  const [expanded, setExpanded] = useState<number | null>(null);

  // Filter state
  const [filterStatus, setFilterStatus] = useState("");
  const [filterOrg, setFilterOrg]       = useState("");
  const [filterQ, setFilterQ]           = useState("");
  const [pendingQ, setPendingQ]         = useState("");

  function load(off: number) {
    setLoading(true);
    setError(null);
    getAiQueries({
      limit: PAGE_SIZE,
      offset: off,
      status: filterStatus || undefined,
      org_id: filterOrg || undefined,
      q: filterQ || undefined,
    })
      .then((r) => { setItems(r.items); setTotal(r.total); })
      .catch((e) => setError(e instanceof Error ? e.message : "Failed to load"))
      .finally(() => setLoading(false));
  }

  // reload when filters change
  useEffect(() => { setOffset(0); load(0); }, [filterStatus, filterOrg, filterQ]); // eslint-disable-line react-hooks/exhaustive-deps
  // reload on page change
  useEffect(() => { load(offset); }, [offset]); // eslint-disable-line react-hooks/exhaustive-deps

  const pages = Math.ceil(total / PAGE_SIZE);
  const page  = Math.floor(offset / PAGE_SIZE) + 1;

  return (
    <div className="animate-fade-in">
      <div className="page-head rise">
        <div>
          <h1>AI Query Log</h1>
          <p className="sub">Every /ai request — what was asked, what tools ran, how many tokens used.</p>
        </div>
      </div>

      {/* Filters */}
      <div className="card card-pad rise" style={{ marginBottom: "16px" }}>
        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "flex-end" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <label className="label">Status</label>
            <select
              className="input"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              style={{ minWidth: "130px" }}
            >
              <option value="">All</option>
              <option value="success">Success</option>
              <option value="unsupported">Unsupported</option>
              <option value="timeout">Timeout</option>
              <option value="unavailable">Unavailable</option>
              <option value="error">Error</option>
            </select>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <label className="label">Org ID</label>
            <input
              className="input"
              placeholder="Filter by org…"
              value={filterOrg}
              onChange={(e) => setFilterOrg(e.target.value)}
              style={{ minWidth: "180px" }}
            />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "4px", flex: 1, minWidth: "200px" }}>
            <label className="label">Search query text</label>
            <div style={{ display: "flex", gap: "6px" }}>
              <input
                className="input"
                placeholder="e.g. EUR to INR…"
                value={pendingQ}
                onChange={(e) => setPendingQ(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") setFilterQ(pendingQ); }}
                style={{ flex: 1 }}
              />
              <button className="btn btn-ghost" onClick={() => setFilterQ(pendingQ)}>Search</button>
              {(filterQ || filterStatus || filterOrg) && (
                <button className="btn btn-ghost" onClick={() => {
                  setFilterStatus(""); setFilterOrg(""); setFilterQ(""); setPendingQ("");
                }}>Clear</button>
              )}
            </div>
          </div>
        </div>
      </div>

      {error && <div className="error-banner" style={{ marginBottom: "14px" }}>{error}</div>}

      {/* Stats bar */}
      {!loading && (
        <p className="muted" style={{ fontSize: "12.5px", marginBottom: "10px" }}>
          {total.toLocaleString()} record{total !== 1 ? "s" : ""}
          {(filterStatus || filterOrg || filterQ) ? " (filtered)" : ""}
          {total > PAGE_SIZE ? ` · page ${page} of ${pages}` : ""}
        </p>
      )}

      {/* Table */}
      <div className="card rise" style={{ overflow: "hidden" }}>
        {loading ? (
          <div className="skeleton" style={{ height: "200px", margin: "16px" }} />
        ) : items.length === 0 ? (
          <p className="muted" style={{ padding: "24px", textAlign: "center" }}>
            No queries found{(filterStatus || filterOrg || filterQ) ? " matching these filters" : ""}.
          </p>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12.5px" }}>
              <thead>
                <tr style={{ background: "var(--surface-raised)", borderBottom: "1px solid var(--border)" }}>
                  {["Time", "Org", "Status", "Intent", "Query", "Tools", "Tokens", "Duration"].map((h) => (
                    <th key={h} style={{ padding: "8px 12px", textAlign: "left", fontWeight: 600, fontSize: "11px", letterSpacing: ".06em", textTransform: "uppercase", color: "var(--text-subtle)", whiteSpace: "nowrap" }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <>
                    <tr
                      key={row.id}
                      onClick={() => setExpanded(expanded === row.id ? null : row.id)}
                      style={{ cursor: "pointer", borderBottom: "1px solid var(--border)", transition: "background .1s" }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "var(--surface-2)")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "")}
                    >
                      <td style={{ padding: "8px 12px", color: "var(--text-subtle)", whiteSpace: "nowrap" }}>
                        {fmtDate(row.created_at)}
                      </td>
                      <td style={{ padding: "8px 12px", whiteSpace: "nowrap" }}>
                        {row.org_id ? (
                          <Link
                            to={`/organizations/${row.org_id}`}
                            onClick={(e) => e.stopPropagation()}
                            style={{ color: "var(--accent-text)", textDecoration: "none", fontFamily: "var(--font-mono)", fontSize: "11.5px" }}
                          >
                            {row.org_id.slice(0, 8)}…
                          </Link>
                        ) : <span style={{ color: "var(--text-subtle)" }}>-</span>}
                      </td>
                      <td style={{ padding: "8px 12px" }}>
                        <span className={`badge ${STATUS_COLORS[row.status] ?? "badge-neutral"}`} style={{ fontSize: "10.5px" }}>
                          {row.status}
                        </span>
                      </td>
                      <td style={{ padding: "8px 12px", color: "var(--text-muted)" }}>
                        {row.intent ?? "-"}
                      </td>
                      <td style={{ padding: "8px 12px", maxWidth: "280px" }}>
                        <span style={{ color: "var(--text)", display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {truncate(row.query, 70)}
                        </span>
                      </td>
                      <td style={{ padding: "8px 12px", textAlign: "right", whiteSpace: "nowrap", color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>
                        {row.tool_calls != null ? (
                          <span title={`${row.total_tool_calls ?? "?"} total (including internal)`}>
                            {row.tool_calls}/{row.total_tool_calls ?? "?"}
                          </span>
                        ) : "-"}
                      </td>
                      <td style={{ padding: "8px 12px", textAlign: "right", whiteSpace: "nowrap", color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>
                        {fmtTokens(row.total_tokens)}
                      </td>
                      <td style={{ padding: "8px 12px", textAlign: "right", whiteSpace: "nowrap", color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>
                        {fmt(row.execution_ms)}
                      </td>
                    </tr>
                    {expanded === row.id && (
                      <tr key={`${row.id}-detail`} style={{ borderBottom: "1px solid var(--border)" }}>
                        <td colSpan={8} style={{ padding: 0 }}>
                          <RowDetail row={row} />
                        </td>
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {pages > 1 && (
        <div style={{ display: "flex", gap: "8px", alignItems: "center", marginTop: "12px", justifyContent: "center" }}>
          <button className="btn btn-ghost" disabled={offset === 0} onClick={() => setOffset(0)}>First</button>
          <button className="btn btn-ghost" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>Prev</button>
          <span className="muted" style={{ fontSize: "13px" }}>Page {page} of {pages}</span>
          <button className="btn btn-ghost" disabled={offset + PAGE_SIZE >= total} onClick={() => setOffset(offset + PAGE_SIZE)}>Next</button>
          <button className="btn btn-ghost" disabled={offset + PAGE_SIZE >= total} onClick={() => setOffset((pages - 1) * PAGE_SIZE)}>Last</button>
        </div>
      )}
    </div>
  );
}
